import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import {
  fetchAccessLogs,
  selectAccessLogsByContext,
  selectAccessLogsByIds,
  selectAuth,
} from "./slice"
import { parseAccessLogsFilters } from "./filters"
import { updateSearchParamsWithFilters } from "../filters/filters"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import ErrorMsg from "../../ui/ErrorMsg"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import { Action, Actions, Header, Overview } from "../../ui/PageHeader"
import LoadingSpinnerInline from "../../ui/LoadingSpinnerInline"
import AccessLogsTable from "./AccessLogsTable"
import DownloadCSVLink from "../observations/DownloadCSVLink"
import { getAccessLogsCSVURL } from "./api"

const PAGE_SIZE = 200 // Default initial and subsequent page size

/* AccessLogList renders a page containing a filter- and sort-able table
 * of all user access logs matching the criteria in the current URL.  */
const AccessLogList = () => {
  const dispatch = useDispatch()

  // auth token for csv download
  const { token } = useSelector(selectAuth)

  // we get any matching access logs
  const accessLogMatches = useSelector((state) =>
    selectAccessLogsByContext(state, "root")
  )
  // We get the full objects from the ids that match
  const accessLogs = useSelector((state) =>
    selectAccessLogsByIds(state, accessLogMatches?.ids || [])
  )

  // Parse parameters into filter object (which drives filters UI state)
  // Note: We are using the URL as the "state" here, and updates
  // perform a history replacement (so as not to mess up "back" button
  // behaviour too much - though we may want to apply a heuristic like
  // small changes replace history, big changes push history)
  const location = useLocation()
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(location.search)
  const filter = parseAccessLogsFilters(urlParams)
  updateSearchParamsWithFilters(urlParams, filter)

  useEffect(() => {
    dispatch(
      fetchAccessLogs({
        filter,
        page: { offset: 0, limit: PAGE_SIZE },
        context: ["root"],
      })
    )
  }, [urlParams.toString()])

  // How we apply a filter update. Filter state is entirely in the URL query parameters
  const setFilter = (filter = {}) => {
    const updUrlParams = updateSearchParamsWithFilters(urlParams, filter)
    navigate({
      pathname: location.pathname,
      search: `?${updUrlParams}`,
    })
  }

  // How many more are available to load?
  const loadableCount = Math.min(
    PAGE_SIZE,
    accessLogs?.length && accessLogMatches?.page?.total_count
      ? accessLogMatches.page.total_count - accessLogs.length
      : 50
  )

  // Event handler for 'Show xxx more' button that was clicked
  const handleLoadMore = () => {
    const lastIndex = accessLogs?.length

    const page = {
      offset: lastIndex,
      limit: loadableCount,
    }
    dispatch(fetchAccessLogs({ filter, page, context: ["root"] }))
  }

  return (
    <Layout>
      <NavMain key="nav-left" />
      <Helmet>
        <title>Access logs - Astrosite</title>
      </Helmet>
      <ScrollablePage>
        {accessLogs.length === 0 &&
          accessLogMatches?._loadStatus === "loading" && (
            <Backdrop>
              <LoadingSpinner />
            </Backdrop>
          )}
        <Header>
          <Overview>
            <h1>Access logs</h1>
            <p>
              {accessLogs.length > 0 && (
                <span>
                  {accessLogMatches?._loadStatus === "loading" && (
                    <LoadingSpinnerInline />
                  )}
                  Showing <strong>{accessLogMatches?.page?.end_index}</strong>
                  {accessLogMatches?.page?.total_count && (
                    <span>
                      {" "}
                      of{" "}
                      <strong className="count total_count">
                        {accessLogMatches.page.total_count}
                      </strong>
                    </span>
                  )}{" "}
                  logs
                </span>
              )}
            </p>
          </Overview>
          <Actions>
            <Action>
              <DownloadCSVLink
                displayCount={accessLogMatches?.page?.total_count || 0}
                filter={filter}
                generateURL={() => getAccessLogsCSVURL(filter, token)}
              />
            </Action>
          </Actions>
        </Header>
        {accessLogMatches?._loadStatus === "rejected" && (
          <ErrorMsg
            msg={"There was a problem fetching access logs"}
            showIcon={true}
          />
        )}
        <Paper>
          <AccessLogsTable
            accessLogs={accessLogs}
            filter={filter}
            setFilter={setFilter}
          />
          {accessLogs?.length === 0 &&
            accessLogMatches?._loadStatus === "idle" && (
              <p>There are no access logs matching your criteria.</p>
            )}
          <footer className="actions">
            {accessLogs.length > 0 &&
              accessLogs.length !== accessLogMatches?.page?.total_count && (
                <>
                  <button
                    onClick={handleLoadMore}
                    disabled={accessLogMatches?._loadStatus === "loading"}
                  >
                    {accessLogMatches?._loadStatus === "loading"
                      ? "Loading..."
                      : `Show ${loadableCount} more`}
                  </button>
                </>
              )}
          </footer>
        </Paper>
      </ScrollablePage>
    </Layout>
  )
}

export default AccessLogList
